/**
 * The chase mail, shown for review before it goes.
 *
 * Remind used to fire `rfq.remind_rfq` straight off the button. That method answers
 * `{sent: true, sent_to: null}` — it resolves no recipient, writes no timestamp and creates no
 * `Communication` (measured live 2026-08-07). So the button reported success and mailed nobody,
 * which is worse than one that fails: the purchaser believes the supplier has been chased and
 * stops waiting for a reply that was never asked for.
 *
 * It now composes a draft the purchaser reads, edits and sends — the same shape as the RFQ send
 * flow (`SendRfqModal`), for the same reason: mail to a supplier is a commercial act, and the
 * person accountable should see the words first.
 *
 * It does NOT reuse that dialog's `crow`/`ceditor` classes. Those are tuned for its dense
 * per-supplier list — a 44px label column and a borderless input, sized for scanning many rows —
 * and this is a single form read once and edited carefully. In 44px, "Reply by" and "Subject"
 * wrapped onto two lines, and `ceditor`'s own `max-height` inside the modal's already-scrolling
 * body left the message clipped mid-sentence with the footer hard against it. `.rmd-row`
 * (global.css) is the letter-shaped alternative: a 72px label column, sharing `.fld`'s input
 * styling.
 *
 * ---- Layout, and one bug worth not reintroducing ----
 *
 * The content is wrapped in `.mb` > `.pad`, as every other `Modal` call site does. Without it
 * `Modal` renders children as bare children of `.box`, which has neither padding nor a scroll
 * cap — so the fields sat flush against the dialog's left edge while the header and footer were
 * inset 18px (three different left edges on one dialog), and a long message grew the box past the
 * bottom of the viewport instead of scrolling. `.fld textarea`'s comment in global.css asserts
 * "the modal body (`.mb`) already scrolls"; for this dialog that was simply untrue, because
 * nothing rendered a `.mb`.
 *
 * The envelope fields lead with WHO and WHAT rather than the message, but they are the part
 * nobody edits — so they are compact, and Cc stays hidden until asked for. The message gets the
 * remaining height because it is the only thing here a purchaser composes.
 *
 * The note is PLAIN TEXT. `send_rfq_email_with_excel` renders `message` as HTML, but asking a
 * purchaser to hand-edit `<p>` tags to reword a sentence is how a malformed mail reaches a
 * supplier — `textToHtml` converts on the way out (`useEnquiryMutations.ts`'s `mailRfq`).
 *
 * The workbook is not re-composed here. `type: 'reminder'` makes the server re-attach the SAME
 * file, so a chase cannot drift from what the supplier was originally asked to price.
 */

import { useState } from 'react'
import { Badge } from '../../components/Badge'
import { Modal } from '../../components/Modal'
import { describeAwaiting, parseAddresses } from '../../lib/rfq'
import type { RfqMessage } from '../../lib/types'

/** The one sentence the date picker owns, so re-picking replaces it instead of stacking copies. */
const DEADLINE_LINE = /\n*We would be grateful for your response by [^\n]*\n*/

/**
 * The threshold at which waiting becomes worth flagging, matching the badge tone this dialog
 * already used. Below it the wait is normal and the line stays quiet; a chip that shouts about an
 * RFQ sent an hour ago teaches people to ignore it.
 */
const LATE_AFTER_DAYS = 3

/** Last path segment of `excel_url` — what the supplier will see attached, not our storage path. */
function fileName(url: string): string {
  const last = url.split('/').pop() ?? ''
  return last === '' ? url : last
}

export function RemindModal({
  draft,
  rfq,
  awaitingDays,
  excelUrl,
  sending,
  onChange,
  onClose,
  onSend,
}: {
  draft: RfqMessage
  rfq: string
  /** Kept in the props for the caller's sake; the threading tag is the RFQ's, not the enquiry's. */
  enquiryId?: string
  awaitingDays: number | null
  /** The RFQ's own workbook, named so the purchaser can see WHICH file goes back out. Optional:
   *  an RFQ generated before the server stored one carries none, and a chip is not worth
   *  inventing a filename for — the prose fallback stays honest. */
  excelUrl?: string | null
  sending: boolean
  onChange: (patch: Partial<RfqMessage>) => void
  onClose: () => void
  onSend: () => void
}) {
  const [replyBy, setReplyBy] = useState('')
  // Opens on demand; `draft.cc` being non-empty forces it open so a pre-filled Cc can never hide.
  const [ccOpen, setCcOpen] = useState(false)

  const noRecipient = draft.to.length === 0
  // The server's `[REQ-<rfq>]` tag is what threads the reply — see `buildReminderDraft`.
  const lostTag = !draft.subject.includes(`[REQ-${rfq}]`)
  const showCc = ccOpen || draft.cc.length > 0
  const late = awaitingDays !== null && awaitingDays >= LATE_AFTER_DAYS

  const setDeadline = (next: string) => {
    setReplyBy(next)
    const stripped = draft.note.replace(DEADLINE_LINE, '\n\n')
    if (!next) {
      onChange({ note: stripped })
      return
    }
    const sentence = `We would be grateful for your response by ${next}.`
    const anchor = 'If you are unable to quote'
    onChange({
      note: stripped.includes(anchor)
        ? stripped.replace(anchor, `${sentence}\n\n${anchor}`)
        : `${stripped.trimEnd()}\n\n${sentence}`,
    })
  }

  return (
    <Modal
      title={`Remind ${draft.supplier}`}
      width={640}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button type="button" className="btn pri" onClick={onSend} disabled={sending || noRecipient}>
            {sending ? 'Sending…' : 'Send reminder'}
          </button>
        </>
      }
    >
      <div className="mb rmd-body">
        <div className="pad">
          {/* Why this dialog is open: which RFQ, when it went out, and whether the silence has
              gone on long enough to matter. The purchaser opened this from a table row and should
              not have to remember which one. */}
          <div className="rmd-why">
            <Badge tone="muted">{rfq}</Badge>
            <span>sent {describeAwaiting(awaitingDays)}</span>
            <span className={late ? 'late' : undefined}>· no reply yet</span>
          </div>

          {/* The exact workbook the supplier gets back, named. `type: 'reminder'` makes the server
              re-attach the original, so this is a statement of fact, not a file picker. */}
          {excelUrl ? (
            <div className="rmd-attach" title={excelUrl}>
              <span aria-hidden="true">📎</span>
              <span className="fn">{fileName(excelUrl)}</span>
              <span>· re-attached</span>
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 16 }}>Original workbook re-attached.</div>
          )}

          {noRecipient ? (
            <div style={{ marginBottom: 12 }}>
              <Badge tone="danger">
                No email address for {draft.supplier} — add one against the supplier before chasing them
              </Badge>
            </div>
          ) : null}

          <div className="rmd-row">
            <label htmlFor="remind-to">To</label>
            <input
              id="remind-to"
              value={draft.to.join(', ')}
              placeholder="comma-separated"
              onChange={(e) => onChange({ to: parseAddresses(e.target.value) })}
            />
          </div>

          {showCc ? (
            <div className="rmd-row">
              <label htmlFor="remind-cc">Cc</label>
              <input
                id="remind-cc"
                value={draft.cc.join(', ')}
                placeholder="comma-separated"
                onChange={(e) => onChange({ cc: parseAddresses(e.target.value) })}
              />
            </div>
          ) : (
            <div className="rmd-row">
              <span aria-hidden="true" />
              <button type="button" className="rmd-addcc" onClick={() => setCcOpen(true)}>
                + Add Cc
              </button>
            </div>
          )}

          <div className="rmd-row">
            <label htmlFor="remind-subject">Subject</label>
            <input id="remind-subject" value={draft.subject} onChange={(e) => onChange({ subject: e.target.value })} />
          </div>

          {lostTag ? (
            <div style={{ fontSize: 12, margin: '0 0 10px 82px' }}>
              <Badge tone="warn">
                ⚠ The subject no longer carries [REQ-{rfq}] — their reply won't thread back to this RFQ
              </Badge>
            </div>
          ) : null}

          <div className="rmd-row">
            <label htmlFor="remind-by">Reply by</label>
            <input id="remind-by" type="date" value={replyBy} onChange={(e) => setDeadline(e.target.value)} />
          </div>

          <div className="fld rmd-note" style={{ marginTop: 16, marginBottom: 0 }}>
            <label htmlFor="remind-note">Message</label>
            <textarea id="remind-note" value={draft.note} onChange={(e) => onChange({ note: e.target.value })} />
          </div>
        </div>
      </div>
    </Modal>
  )
}
